import { useEffect, useState } from 'react';

import { SECURE_API } from 'src/api/api';
import { SignupTimelinePoint, SignupTimelineResponse } from 'src/interfaces/SignupTimeline';

interface UseSignupTimelineParams {
  editionId?: string | null;
  competitionId?: string | null;
  distanceId?: string | null;
  /** When false, skip fetch (e.g. parent supplies series). */
  enabled?: boolean;
}

interface UseSignupTimelineResult {
  points: SignupTimelinePoint[];
  isLoading: boolean;
  error: string | null;
}

export function useSignupTimeline({
  editionId,
  competitionId,
  distanceId,
  enabled = true,
}: UseSignupTimelineParams): UseSignupTimelineResult {
  const [points, setPoints] = useState<SignupTimelinePoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !editionId) {
      setPoints([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    SECURE_API.get<SignupTimelineResponse>('registrations/signup-timeline', {
      params: {
        editionId,
        ...(competitionId ? { competitionId } : {}),
        ...(distanceId ? { distanceId } : {}),
      },
    })
      .then(({ data }) => {
        if (!cancelled) {
          setPoints(data.points ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPoints([]);
          setError('fetch');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, editionId, competitionId, distanceId]);

  return { points, isLoading, error };
}
